/**
 * @fileoverview
 * Notification API Service - HTTP calls for in-app notifications (list, mark read).
 * No feature logic; only HTTP and DTO typing.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject } from '@angular/core';
import { Observable, tap, catchError, of, map } from 'rxjs';
import { ApiService } from '@core/http/api.service';
import { InAppNotificationStore } from '@core/state/stores/in-app-notification.store';
import {
  notificationDtoToItem,
  NotificationApiDto,
} from '@core/state/adapters/in-app-notification.adapter';
import { InAppNotificationItem } from '@core/state/models/in-app-notification.model';

export const NOTIFICATION_PAGE_SIZE = 20;

export interface NotificationsListResponse {
  data: NotificationApiDto[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    unreadCount: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationApiService {
  private readonly api = inject(ApiService);
  private readonly store = inject(InAppNotificationStore);

  /**
   * GET /notifications (paginated). Returns raw response.
   */
  getNotifications(params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  } = {}): Observable<NotificationsListResponse> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const sortBy = params.sortBy ?? 'createdAt';
    const sortOrder = params.sortOrder ?? 'DESC';
    const query = `?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    return this.api.get<NotificationsListResponse>(`/notifications${query}`);
  }

  /**
   * PATCH /notifications/:id/read
   */
  markAsRead(id: number): Observable<NotificationApiDto | null> {
    return this.api.patch<NotificationApiDto>(`/notifications/${id}/read`, {}).pipe(
      catchError(() => of(null)),
    );
  }

  /**
   * POST /notifications/read-all. Returns { updated: number }.
   */
  markAllAsRead(): Observable<{ updated: number }> {
    return this.api.post<{ updated: number }>('/notifications/read-all', {}).pipe(
      catchError(() => of({ updated: 0 })),
    );
  }

  /**
   * Load notification history and merge into store. Call when user is logged in (e.g. with WS connect).
   * Handles both wrapped format { data, meta } and direct array (response interceptor may unwrap differently).
   */
  loadHistory(): Observable<InAppNotificationItem[]> {
    return this.getNotifications({ page: 1, limit: NOTIFICATION_PAGE_SIZE }).pipe(
      tap((res) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        const items = list.map((dto: NotificationApiDto) => notificationDtoToItem(dto));
        this.store.setItems(items);
        const page = 1;
        const totalPages =
          !Array.isArray(res) && res?.meta != null
            ? res.meta.totalPages
            : Math.max(1, Math.ceil((list?.length ?? 0) / NOTIFICATION_PAGE_SIZE) || 1);
        this.store.setPaginationMeta({ page, totalPages });
      }),
      map((res) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        return list.map((dto: NotificationApiDto) => notificationDtoToItem(dto));
      }),
      catchError(() => of([])),
    );
  }

  /**
   * Load next page of notifications and append to store. Call when user scrolls near bottom.
   */
  loadMoreNotifications(): Observable<InAppNotificationItem[]> {
    const nextPage = this.store.currentNotificationPage() + 1;
    return this.getNotifications({
      page: nextPage,
      limit: NOTIFICATION_PAGE_SIZE,
    }).pipe(
      tap((res) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        const items = list.map((dto: NotificationApiDto) => notificationDtoToItem(dto));
        this.store.appendItems(items);
        const nextPage = this.store.currentNotificationPage() + 1;
        const totalPages =
          !Array.isArray(res) && res?.meta != null
            ? res.meta.totalPages
            : nextPage;
        this.store.setPaginationMeta({ page: nextPage, totalPages });
      }),
      map((res) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        return list.map((dto: NotificationApiDto) => notificationDtoToItem(dto));
      }),
      catchError(() => of([])),
    );
  }
}
